import { when } from "@legendapp/state";
import { toast } from "sonner";

import { authClient } from "~/libs/auth.lib";
import { processAvatarImage } from "~/libs/image.lib";
import { trpc } from "~/libs/trpc.lib";
import { activeOrganization$ } from "./active-organization.store";

export const createOrganization = async (name: string, slug: string) => {
  const { error } = await authClient.organization.create({
    name: name.trim(),
    slug: slug.trim(),
  });

  if (error) {
    toast.error(error.message || "Failed to create organization");
    throw new Error(error.message, { cause: error });
  }

  // Wait for the active organization to be set before returning
  await when(activeOrganization$.id);
};

export const updateOrganization = async (
  organizationId: string,
  name: string,
  slug: string,
) => {
  const { error } = await authClient.organization.update({
    organizationId,
    data: {
      name: name.trim(),
      slug: slug.trim(),
    },
  });

  if (error) {
    toast.error(error.message || "Failed to update organization");
    throw new Error(error.message, { cause: error });
  }

  // better-auth automatically updates its atoms, which sync to our store
  toast.success("Organization updated successfully");
};

export const isOrganizationSlugAvailable = async (
  slug: string,
): Promise<boolean> => {
  const { data, error } = await authClient.organization.checkSlug({
    slug,
  });

  if (data?.status) {
    return true;
  }

  if (error?.code === "SLUG_IS_TAKEN") {
    return false;
  }

  // Unknown error
  throw new Error(error?.message || "Unknown slug verification error");
};

/** Processes an image file, uploads it to GCS, and updates the org logo. */
export const updateOrganizationLogo = async (
  organizationId: string,
  file: File,
) => {
  const blob = await processAvatarImage(file);

  const { uploadUrl, publicUrl } =
    await trpc.organizations.getLogoUploadUrl.mutate();

  const uploadResponse = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": "image/webp" },
    body: blob,
  });

  if (!uploadResponse.ok) {
    toast.error("Failed to upload avatar image");
    throw new Error(`GCS upload failed: ${uploadResponse.status}`);
  }

  const { error } = await authClient.organization.update({
    organizationId,
    data: { logo: publicUrl },
  });

  if (error) {
    toast.error(error.message || "Failed to update organization logo");
    throw new Error(error.message, { cause: error });
  }

  toast.success("Organization avatar updated");
};

/**
 * Creates the shared Slack Connect channel for the active org and optimistically
 * updates `activeOrganization$` so the UI flips to the connected state without
 * waiting for better-auth's atom to refetch. Server-first: the tRPC mutation
 * owns the Slack work + DB write, then the local store mirrors the result.
 */
export const connectSlackChannel = async () => {
  try {
    const { channelId, channelName, connectedAt, inviteUrl, inviteSentAt } =
      await trpc.slack.createSharedChannel.mutate();

    activeOrganization$.slackChannelId.set(channelId);
    activeOrganization$.slackChannelName.set(channelName ?? undefined);
    activeOrganization$.slackChannelConnectedAt.set(connectedAt ?? undefined);
    activeOrganization$.slackInviteUrl.set(inviteUrl ?? undefined);
    activeOrganization$.slackInviteSentAt.set(inviteSentAt ?? undefined);

    toast.success("Shared Slack channel created");
  } catch (err) {
    toast.error("Failed to create the shared Slack channel");
    throw err;
  }
};

/**
 * Re-sends Slack Connect invitations to all current org members and regenerates
 * the shareable join link (invites expire ~30 days). Optimistically updates
 * `activeOrganization$` so the card's "sent" timestamp + link refresh.
 */
export const resendSlackInvites = async () => {
  try {
    const { inviteUrl, inviteSentAt } = await trpc.slack.resendInvites.mutate();

    if (inviteUrl) activeOrganization$.slackInviteUrl.set(inviteUrl);
    activeOrganization$.slackInviteSentAt.set(inviteSentAt ?? undefined);

    toast.success("Slack invitation resent");
  } catch (err) {
    toast.error("Failed to resend the Slack invitation");
    throw err;
  }
};

/** Switches the active organization and performs a full page reload to reset all stores. */
export const setActiveOrganization = async (organizationId: string) => {
  const { error } = await authClient.organization.setActive({
    organizationId,
  });

  if (error) {
    toast.error(error.message || "Failed to switch organization");
    throw new Error(error.message, { cause: error });
  }

  window.location.href = "/";
};
